import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { get } from '../../services/apiService';

/**
 * Test Function and Automation Status Pivot Table component
 * Displays a pivot table of tests by Function (component) and Automation Status
 * @returns {JSX.Element} Test Function Automation Pivot Table component
 */
const TestFunctionAutomationPivotTable = () => {
  const [pivotData, setPivotData] = useState({});
  const [functions, setFunctions] = useState([]);
  const [automationStatuses, setAutomationStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    const fetchPivotData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await get('/api/test/public/function-automation-pivot');

        if (response.success) {
          const data = response.data || [];
          const statuses = response.automationStatuses || [];

          // Build pivot data structure
          const pivotObj = {};
          const funcs = [];

          data.forEach(item => {
            const func = item.function || 'Unknown';
            funcs.push(func);
            pivotObj[func] = {};

            // Initialize all automation statuses with 0
            statuses.forEach(status => {
              pivotObj[func][status] = 0;
            });

            // Fill in actual counts
            Object.keys(item.automationStatusCounts || {}).forEach(status => {
              if (statuses.includes(status)) {
                pivotObj[func][status] = item.automationStatusCounts[status];
              }
            });
          });

          setFunctions(funcs.sort());
          setAutomationStatuses(statuses);
          setPivotData(pivotObj);
        } else {
          throw new Error(response.message || 'Failed to fetch pivot data');
        }
      } catch (err) {
        console.error('Error fetching pivot data:', err);
        setError(err.message || 'Failed to load pivot table data');
      } finally {
        setLoading(false);
      }
    };

    fetchPivotData();
  }, []);

  // Calculate totals for each function and automation status
  const calculateTotals = () => {
    if (!pivotData || Object.keys(pivotData).length === 0) {
      return { rowTotals: {}, columnTotals: {} };
    }

    const rowTotals = {};
    const columnTotals = {};

    // Initialize column totals
    automationStatuses.forEach(status => {
      columnTotals[status] = 0;
    });

    // Calculate row and column totals
    functions.forEach(func => {
      rowTotals[func] = 0;
      automationStatuses.forEach(status => {
        const value = pivotData[func]?.[status] || 0;
        rowTotals[func] += value;
        columnTotals[status] += value;
      });
    });

    return { rowTotals, columnTotals };
  };

  const { rowTotals, columnTotals } = calculateTotals();

  // Handle column header click for sorting
  const handleSort = (status) => {
    if (sortColumn === status) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(status);
      setSortDirection('asc');
    }
  };

  // Sort functions based on the selected column's count
  const sortedFunctions = useMemo(() => {
    if (!sortColumn || !pivotData) {
      return functions;
    }

    const sorted = [...functions].sort((a, b) => {
      const valueA = pivotData[a]?.[sortColumn] || 0;
      const valueB = pivotData[b]?.[sortColumn] || 0;
      
      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });

    return sorted;
  }, [functions, pivotData, sortColumn, sortDirection]);

  // Get color based on value (higher values = darker green)
  const getCellColor = (value) => {
    if (!value || value === 0) return '#FFFFFF';

    // Define color ranges
    if (value < 5) return '#E8F5E9';
    if (value < 10) return '#C8E6C9';
    if (value < 20) return '#A5D6A7';
    if (value < 30) return '#81C784';
    if (value < 50) return '#66BB6A';
    if (value < 75) return '#4CAF50';
    if (value < 100) return '#43A047';
    return '#2E7D32';
  };

  // Get text color based on background color (for contrast)
  const getTextColor = (bgColor) => {
    // For darker greens, use white text
    if (bgColor === '#43A047' || bgColor === '#2E7D32') return '#FFFFFF';
    return '#000000';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!pivotData || Object.keys(pivotData).length === 0) {
    return (
      <Box mt={2}>
        <Typography variant="subtitle1" color="textSecondary">
          No data available for pivot table
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Function</TableCell>
              {automationStatuses.map(status => (
                <TableCell 
                  key={status} 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#e0e0e0'
                    }
                  }}
                  onClick={() => handleSort(status)}
                >
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    {status}
                    {sortColumn === status && (
                      sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFunctions.map(func => (
              <TableRow key={func} hover>
                <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                  {func}
                </TableCell>
                {automationStatuses.map(status => {
                  const value = pivotData[func]?.[status] || 0;
                  const bgColor = getCellColor(value);
                  const textColor = getTextColor(bgColor);
                  
                  return (
                    <TableCell 
                      key={`${func}-${status}`} 
                      align="center"
                      sx={{ 
                        backgroundColor: bgColor,
                        color: textColor
                      }}
                    >
                      {value}
                    </TableCell>
                  );
                })}
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#e0e0e0'
                  }}
                >
                  {rowTotals[func] || 0}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Total</TableCell>
              {automationStatuses.map(status => (
                <TableCell 
                  key={`total-${status}`} 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#e0e0e0'
                  }}
                >
                  {columnTotals[status] || 0}
                </TableCell>
              ))}
              <TableCell 
                align="center" 
                sx={{ 
                  fontWeight: 'bold', 
                  backgroundColor: '#bdbdbd'
                }}
              >
                {Object.values(columnTotals).reduce((sum, val) => sum + val, 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TestFunctionAutomationPivotTable;

